import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { requestPasswordReset } from "@/lib/identity/identity-api";
import { friendlyNetworkError } from "@/lib/http/network-error";

const SUCCESS_MSG =
  "If that email is registered, a VedaPath password reset link has been sent. Check your inbox (and spam).";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const data = await requestPasswordReset(email);
      if (data.email_sent === false) {
        const reason =
          typeof data.email_reason === "string" ? data.email_reason : "Email delivery failed.";
        if (/RESEND_API_KEY|EMAIL_FROM/i.test(reason)) {
          setError(
            `We could not send the email (${reason}). Ask admin to set Worker secrets EMAIL_FROM and RESEND_API_KEY, then redeploy.`
          );
        } else {
          setError(`We could not send the email (${reason}).`);
        }
      } else {
        setInfo(SUCCESS_MSG);
      }
    } catch (err) {
      const apiMsg = err.response?.data?.error || err.response?.data?.detail;
      if (typeof apiMsg === "string" && apiMsg.trim()) {
        setError(apiMsg);
      } else {
        setError(friendlyNetworkError(err, "Could not send reset link."));
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-24" data-testid="forgot-password-page">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="font-heading text-2xl text-center">Reset password</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
            {info && <p className="text-sm text-muted-foreground">{info}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Send reset link"}
            </Button>
          </form>
          <p className="text-center text-sm mt-6">
            <Link to="/login" className="text-primary">
              Back to sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
